/**
 * Composition de l'image partagée sur un canvas carré.
 *
 * Dessin déterministe : pour un même `ShareCardData` et un même fond, le rendu
 * est identique au pixel près. Indépendant du thème de l'app (mode sombre /
 * saison) puisque toutes les couleurs sont figées ici — l'image se regarde hors
 * de l'application.
 */

import { layoutTitle, type Measurer } from '@/lib/share/textLayout';
import type { ShareCardData } from '@/lib/share/eventCardData';
import type { ShareGradient } from '@/lib/share/backgrounds';
import type { ShareLogo } from '@/lib/share/assets';

const PAPER = '#FBF7EF';
const CHAMPAGNE = '#C89B5A';
const TITLE_SIZES = [128, 116, 104, 92, 80, 72, 64, 56];

export type DrawBackground =
  | { kind: 'photo'; image: CanvasImageSource; iw: number; ih: number }
  | { kind: 'gradient'; gradient: ShareGradient };

export interface DrawParams {
  data: ShareCardData;
  background: DrawBackground;
  accent: string;
  logo: ShareLogo | null;
}

function hexToRgb(hex: string): [number, number, number] {
  const value = Number.parseInt(hex.replace('#', ''), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function rgba(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(x, y, w, h, r);
    return;
  }
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawPhotoBackground(
  ctx: CanvasRenderingContext2D,
  bg: Extract<DrawBackground, { kind: 'photo' }>,
  S: number,
): void {
  // Photo en « cover » (recadrage centré).
  const scale = Math.max(S / bg.iw, S / bg.ih);
  const w = bg.iw * scale;
  const h = bg.ih * scale;
  ctx.drawImage(bg.image, (S - w) / 2, (S - h) / 2, w, h);

  // Voiles sombres pour garder le texte lisible quelle que soit la photo :
  // teinte chaude globale + haut (date) + bas (logo/mention) + gauche (titre).
  ctx.fillStyle = 'rgba(20, 17, 14, 0.22)';
  ctx.fillRect(0, 0, S, S);
  const top = ctx.createLinearGradient(0, 0, 0, S * 0.45);
  top.addColorStop(0, 'rgba(0, 0, 0, 0.55)');
  top.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = top;
  ctx.fillRect(0, 0, S, S * 0.45);
  const bottom = ctx.createLinearGradient(0, S * 0.48, 0, S);
  bottom.addColorStop(0, 'rgba(0, 0, 0, 0)');
  bottom.addColorStop(1, 'rgba(0, 0, 0, 0.82)');
  ctx.fillStyle = bottom;
  ctx.fillRect(0, S * 0.48, S, S * 0.52);
  const left = ctx.createLinearGradient(0, 0, S * 0.62, 0);
  left.addColorStop(0, 'rgba(0, 0, 0, 0.5)');
  left.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = left;
  ctx.fillRect(0, 0, S * 0.62, S);
}

function fillBackground(ctx: CanvasRenderingContext2D, params: DrawParams, S: number): void {
  if (params.background.kind === 'photo') {
    drawPhotoBackground(ctx, params.background, S);
    return;
  }
  const { accent } = params;
  const gradient = params.background.gradient;
  // 1. Dégradé sombre de base — la garantie de lisibilité.
  const rad = (gradient.angle * Math.PI) / 180;
  const dx = Math.cos(rad);
  const dy = Math.sin(rad);
  const half = (S / 2) * (Math.abs(dx) + Math.abs(dy));
  const base = ctx.createLinearGradient(S / 2 - dx * half, S / 2 - dy * half, S / 2 + dx * half, S / 2 + dy * half);
  const last = gradient.stops.length - 1;
  gradient.stops.forEach((color, i) => base.addColorStop(last === 0 ? 0 : i / last, color));
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, S, S);

  // 2. Halo de la catégorie — teinte l'ensemble sans toucher au contraste.
  const glow = ctx.createRadialGradient(S * 0.72, S * 0.26, 0, S * 0.72, S * 0.26, S * 0.95);
  glow.addColorStop(0, rgba(accent, 0.32));
  glow.addColorStop(1, rgba(accent, 0));
  ctx.globalCompositeOperation = 'screen';
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, S, S);
  ctx.globalCompositeOperation = 'source-over';

  // 3. Voiles sombres haut/bas — sécurisent la lisibilité des coins.
  const top = ctx.createLinearGradient(0, 0, 0, S * 0.32);
  top.addColorStop(0, 'rgba(0, 0, 0, 0.24)');
  top.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = top;
  ctx.fillRect(0, 0, S, S * 0.32);
  const bottom = ctx.createLinearGradient(0, S * 0.58, 0, S);
  bottom.addColorStop(0, 'rgba(0, 0, 0, 0)');
  bottom.addColorStop(1, 'rgba(0, 0, 0, 0.5)');
  ctx.fillStyle = bottom;
  ctx.fillRect(0, S * 0.58, S, S * 0.42);
}

function drawHeader(ctx: CanvasRenderingContext2D, data: ShareCardData, S: number, P: number): void {
  const k = S / 1080;
  // Date — police éditoriale italique.
  const dateSize = Math.round(52 * k);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = PAPER;
  ctx.font = `italic ${dateSize}px "Instrument Serif"`;
  ctx.fillText(data.dateLabel, P, P + dateSize);

  // Filet doré — signature de marque.
  const filetY = P + dateSize + Math.round(26 * k);
  ctx.fillStyle = CHAMPAGNE;
  ctx.fillRect(P, filetY, Math.round(104 * k), Math.max(2, Math.round(2 * k)));

  // Ville, plus discrète.
  if (data.cityLabel) {
    const citySize = Math.round(25 * k);
    ctx.font = `500 ${citySize}px "Geist"`;
    ctx.fillStyle = rgba(PAPER, 0.74);
    setTracking(ctx, '0.06em');
    ctx.fillText(data.cityLabel, P, filetY + Math.round(20 * k) + citySize);
    setTracking(ctx, '0em');
  }
}

function setTracking(ctx: CanvasRenderingContext2D, value: string): void {
  const c = ctx as CanvasRenderingContext2D & { letterSpacing?: string };
  if ('letterSpacing' in c) c.letterSpacing = value;
}

function drawPill(
  ctx: CanvasRenderingContext2D,
  text: string,
  rightX: number,
  topY: number,
  opts: { size: number; fill?: string; border?: string; color: string },
): number {
  ctx.font = `500 ${opts.size}px "Geist Mono"`;
  ctx.textAlign = 'left';
  setTracking(ctx, '0.08em');
  const padX = Math.round(opts.size * 0.9);
  const padY = Math.round(opts.size * 0.6);
  const textW = ctx.measureText(text).width;
  const w = textW + padX * 2;
  const h = opts.size + padY * 2;
  const x = rightX - w;
  roundRectPath(ctx, x, topY, w, h, h / 2);
  if (opts.fill) {
    ctx.fillStyle = opts.fill;
    ctx.fill();
  }
  if (opts.border) {
    ctx.lineWidth = Math.max(1, Math.round(opts.size * 0.07));
    ctx.strokeStyle = opts.border;
    ctx.stroke();
  }
  ctx.fillStyle = opts.color;
  ctx.textBaseline = 'middle';
  ctx.fillText(text, x + padX, topY + h / 2 + Math.round(opts.size * 0.06));
  setTracking(ctx, '0em');
  return h;
}

function drawPills(ctx: CanvasRenderingContext2D, data: ShareCardData, accent: string, S: number, P: number): void {
  const k = S / 1080;
  const size = Math.round(22 * k);
  const rightX = S - P;
  const catH = drawPill(ctx, data.categoryLabel, rightX, P, {
    size,
    border: rgba(PAPER, 0.32),
    color: rgba(PAPER, 0.92),
  });
  if (data.priceLabel) {
    drawPill(ctx, data.priceLabel.toUpperCase(), rightX, P + catH + Math.round(14 * k), {
      size,
      fill: rgba(accent, 0.18),
      border: rgba(accent, 0.55),
      color: PAPER,
    });
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

function drawTitle(ctx: CanvasRenderingContext2D, data: ShareCardData, S: number, P: number): void {
  const k = S / 1080;
  const sizes = TITLE_SIZES.map((s) => Math.round(s * k));
  const titleFont = (fs: number) => `${fs}px "Instrument Serif"`;
  const measure: Measurer = (text, fs) => {
    ctx.font = titleFont(fs);
    return ctx.measureText(text).width;
  };
  // Le titre est aligné à gauche sur la marge P : pour qu'aucune ligne ne dépasse
  // les 3/4 de la largeur, sa largeur utile s'arrête au 3/4 moins cette marge.
  const maxWidth = S * 0.75 - P;
  const { fontSize, lines } = layoutTitle(data.title, { maxWidth, maxLines: 4, sizes, measure });

  const lineH = fontSize * 1.08;
  const blockH = lineH * lines.length;
  const regionTop = P + Math.round(232 * k);
  const regionBottom = S - P - Math.round(150 * k);
  const top = clamp((regionTop + regionBottom) / 2 - blockH / 2, regionTop, regionBottom - blockH);

  ctx.font = titleFont(fontSize);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = PAPER;
  ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
  ctx.shadowBlur = Math.round(20 * k);
  ctx.shadowOffsetY = Math.round(2 * k);
  lines.forEach((line, i) => ctx.fillText(line, P, top + i * lineH + fontSize * 0.82));
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
}

function drawFooter(ctx: CanvasRenderingContext2D, logo: ShareLogo | null, S: number, P: number): void {
  const k = S / 1080;
  // Logo LaFête, blanc, en bas à gauche.
  const logoH = Math.round(56 * k);
  if (logo) {
    const logoW = Math.round(logoH * (logo.width / logo.height));
    ctx.drawImage(logo.source, P, S - P - logoH, logoW, logoH);
  }

  // Mention discrète, en bas à droite, alignée à droite sur deux lignes.
  const size = Math.round(23 * k);
  ctx.font = `400 ${size}px "Geist"`;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = rgba(PAPER, 0.66);
  const lineH = size * 1.3;
  const baseline = S - P - Math.round(6 * k);
  ctx.fillText("sur l'application Gazette", S - P, baseline);
  ctx.fillText('Tous les événements', S - P, baseline - lineH);
}

/** Dessine la carte complète dans le contexte fourni (canvas carré de côté `size`). */
export function drawEventCard(ctx: CanvasRenderingContext2D, params: DrawParams, size: number): void {
  const P = Math.round(88 * (size / 1080));
  ctx.clearRect(0, 0, size, size);
  fillBackground(ctx, params, size);
  drawHeader(ctx, params.data, size, P);
  drawPills(ctx, params.data, params.accent, size, P);
  drawTitle(ctx, params.data, size, P);
  drawFooter(ctx, params.logo, size, P);
}
